/**
 * Site Configuration
 * Admin can configure the website behavior here
 */

export interface SiteConfig {
  /**
   * Site mode:
   * - 'category': Display homepage with categories (Horror Games, Action Games, etc.)
   * - 'single-game': Display a specific game as the homepage
   */
  siteMode: 'category' | 'single-game';

  /**
   * Main game slug (only used when siteMode is 'single-game')
   * Example: 'retro-bowl', 'block-blast', 'basketball-legends'
   * 
   * The system will:
   * 1. Try to load {gameSlug}.json from content/games/
   * 2. If not found, fallback to home.json (switch to category mode)
   * 3. If home.json not found, use the first available game
   */
  gameSlug?: string;

  /**
   * Site metadata
   */
  siteName: string;
  siteDescription: string;
  siteUrl: string;
}

/**
 * Game Recommendation Limits
 */
export const GAME_LIMITS = {
  SIDEBAR_GAMES: 15,           // Number of games in sidebar
  MORE_GAMES: 12,              // Number of "more games like this"
  LATEST_GAMES_POOL: 50,       // Pool size for latest games selection
  FEATURED_DISPLAY: 100,       // Max featured games to display
} as const;

/**
 * Supported Languages Configuration
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', hreflang: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ar', hreflang: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'bg', hreflang: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'bn', hreflang: 'bn', name: 'বাংলা', flag: '🇧🇩' },
  { code: 'cs', hreflang: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'da', hreflang: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'de', hreflang: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'el', hreflang: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'es', hreflang: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fi', hreflang: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'fr', hreflang: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'he', hreflang: 'he', name: 'עברית', flag: '🇮🇱' },
  { code: 'hi', hreflang: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'hu', hreflang: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'id', hreflang: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'it', hreflang: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', hreflang: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', hreflang: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'ms', hreflang: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'nl', hreflang: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'no', hreflang: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'pl', hreflang: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'pt-br', hreflang: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'ro', hreflang: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'ru', hreflang: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'sk', hreflang: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'sr', hreflang: 'sr', name: 'Српски', flag: '🇷🇸' },
  { code: 'sv', hreflang: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'th', hreflang: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'tl', hreflang: 'tl', name: 'Filipino', flag: '🇵🇭' },
  { code: 'tr', hreflang: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', hreflang: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'uz', hreflang: 'uz', name: "O'zbek", flag: '🇺🇿' },
  { code: 'vi', hreflang: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'zh', hreflang: 'zh', name: '中文', flag: '🇨🇳' }
] as const;

/**
 * Maps language code → URL path prefix used for hreflang alternate URLs.
 * English uses root '/', other languages use a path prefix.
 * These paths are used to generate <link rel="alternate" hreflang> tags.
 */
export const LANGUAGE_PATH_MAP: Record<string, string> = {
  'en': '/',
  'ar': '/ar',
  'bg': '/bg',
  'bn': '/bn',
  'cs': '/cz',
  'da': '/dk',
  'de': '/de',
  'el': '/gr',
  'es': '/es',
  'fi': '/fi',
  'fr': '/fr',
  'he': '/il',
  'hi': '/hi',
  'hu': '/hu',
  'id': '/id',
  'it': '/it',
  'ja': '/jp',
  'ko': '/kr',
  'ms': '/ms',
  'nl': '/nl',
  'no': '/no',
  'pl': '/pl',
  'pt-br': '/br',
  'ro': '/ro',
  'ru': '/ru',
  'sk': '/sk',
  'sr': '/sr',
  'sv': '/se',
  'th': '/th',
  'tl': '/tl',
  'tr': '/tr',
  'uk': '/ua',
  'uz': '/uz',
  'vi': '/vn',
  'zh': '/zh',
};

/**
 * Reverse map: URL path segment → language code.
 * Used to detect a locale prefix from the URL (e.g. 'vn' → 'vi', 'jp' → 'ja').
 * Derived automatically from LANGUAGE_PATH_MAP.
 */
export const PATH_TO_LANGUAGE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(LANGUAGE_PATH_MAP)
    .filter(([, path]) => path !== '/')
    .map(([lang, path]) => [path.slice(1), lang])
);

/**
 * Game Player Configuration
 */
export const GAME_PLAYER = {
  DEFAULT_ASPECT_RATIO: '56.25%',  // 16:9 ratio
  DEFAULT_WIDTH: '100%',
  DEFAULT_HEIGHT: 600,
  LOADING_TEXT: 'Loading game...',
  PLAY_BUTTON_TEXT: 'PLAY NOW',
} as const;

/**
 * SEO Configuration
 */
export const SEO_CONFIG = {
  DEFAULT_IMAGE: '/assets/upload/pokerogue/webp/pokerogue.webp',
  TITLE_SEPARATOR: ' | ',
  TITLE_SUFFIX: 'PokéRogue',

  ROBOTS_META: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  OG_LOCALE: 'en_US',
  TWITTER_CARD_TYPE: 'summary_large_image',

  // Specific robots meta for different page types
  ROBOTS_META_OVERRIDES: {
    'terms': 'noindex, nofollow',
    'dmca': 'noindex, nofollow',
    'privacy': 'noindex, nofollow',
    'privacy-policy': 'noindex, nofollow',
    'terms-of-service': 'noindex, nofollow',
  }
} as const;

/**
 * Helper function to get robots meta for a specific page
 * @param pageSlug - The page slug (e.g., 'terms', 'dmca', 'privacy')
 * @returns Appropriate robots meta tag content
 */
export function getRobotsMeta(pageSlug?: string): string {
  if (pageSlug && pageSlug in SEO_CONFIG.ROBOTS_META_OVERRIDES) {
    return SEO_CONFIG.ROBOTS_META_OVERRIDES[pageSlug as keyof typeof SEO_CONFIG.ROBOTS_META_OVERRIDES];
  }
  return SEO_CONFIG.ROBOTS_META;
}

/**
 * Content Loading Configuration
 */
export const CONTENT_CONFIG = {
  EXCERPT_MAX_LENGTH: 180,
  MAX_CATEGORY_TAGS: 5,
  CACHE_ENABLED: true,
} as const;

/**
 * ADMIN CONFIGURATION
 * Change these values to configure your site
 */
export const siteConfig: SiteConfig = {
  // Change to 'single-game' for single game site, or 'category' for category site
  siteMode: 'single-game',
  gameSlug: "pokerogue",

  // Site metadata
  siteName: 'PokéRogue',
  siteDescription: 'PokéRogue takes everything you love about the Pokémon universe and mixes it with thrilling roguelite gameplay. No sign-up required — play free instantly!',
  siteUrl: 'https://playpokerogue.com',
};

// DOM Element IDs
export const DOM_IDS = {
  ROOT: 'root',
  COMMENTS_WIDGET: 'comments-widget',
} as const;

// Special category slugs (not regular game categories)
export const SPECIAL_CATEGORIES = ['featured', 'latest', 'trending'] as const;
export type SpecialCategory = typeof SPECIAL_CATEGORIES[number];

// Google Analytics Configuration
export const GOOGLE_ANALYTICS_ID = 'G-73NK0B23CC';
